/**
 * The typed HTTP client.
 *
 * Mirrors the two request helpers the live page has today, `api()` and
 * `apiRaw()`, because those two carry rules that took real incidents to learn.
 * The rules, and why each exists:
 *
 *   1. Send a bearer only when there IS one. An OAuth sign-in, and every
 *      magic-link login after consumeTokenMiddleware strips ?token= from the
 *      URL, leaves an HttpOnly cookie and nothing JS can read. The server reads
 *      the Authorization header BEFORE the cookie, so sending a header built
 *      from a null token makes it authenticate against the string "null" and
 *      answer 401 with a perfectly good cookie on the same request. That is not
 *      hypothetical: apiRaw did exactly this, and it broke "Confirm & send" for
 *      every magic-link user.
 *
 *   2. 401 and 403 mean the session is gone, not that the call failed. They get
 *      their own error type so the app can route to sign-in once, centrally,
 *      instead of every caller pattern-matching on a status code.
 *
 *   3. Some callers need the status, not an exception - the pay sheet renders a
 *      different sentence for 402, 409, 202 and 502, and must NOT bounce the
 *      client to sign-in mid-payment. `raw()` is for them, and deliberately
 *      does not throw on 401.
 *
 * @module web/api/client
 */

import { isHtml2xx } from "./htmlGuard.ts";

/** A non-2xx answer the caller did not opt to handle itself. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

/**
 * The session is gone: 401 or 403.
 *
 * Separate from ApiError so one place can decide what that means. The old page
 * called showError() from inside its fetch helper, which is why the message
 * lived in the network layer and could not be changed per screen.
 */
export class SessionExpiredError extends ApiError {
  constructor(status: number, body: unknown) {
    super(status, body, "Session expired");
    this.name = "SessionExpiredError";
  }
}

/** Credentials are valid, but the selected brand is no longer accessible. */
export class MembershipRequiredError extends ApiError {
  constructor(status: number, body: unknown) {
    super(status, body, "Brand access changed");
    this.name = "MembershipRequiredError";
  }
}

/**
 * The bearer token, when there is one.
 *
 * Only ever set from a `?token=` in the URL or a fresh registration. Absent is
 * the normal state, not a failure - see rule 1 above.
 */
let authToken: string | null = null;
let activeBrandId: string | null = null;
let pendingFinancialRequests = 0;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Per-tab brand selection. Each request snapshots it before awaiting fetch. */
export function setActiveBrandId(id: string | null): void {
  activeBrandId = id;
}

export function hasPendingFinancialRequest(): boolean {
  return pendingFinancialRequests > 0;
}

/** Headers for one request: bearer if we have one, JSON, caller wins. */
function headersFor(path: string, extra?: HeadersInit): HeadersInit {
  return {
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    "Content-Type": "application/json",
    ...(extra ?? {}),
    ...(activeBrandId && !path.startsWith("/api/auth/") ? { "X-Brand-Id": activeBrandId } : {}),
  };
}

/*
 * `isHtml2xx` lives in ./htmlGuard.ts, imported above.
 *
 * It guards BOTH helpers here: `raw()` is the pay sheet's settle call, and it
 * reads `status === 200` as "Confirmed". An HTML 200 there would report a
 * payment that never happened.
 */

/** Parse a JSON body, or null when there is not one. Never throws. */
async function parse(res: Response): Promise<unknown> {
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("json")) return null;
  try {
    return await res.json();
  } catch {
    // 204, an empty body, or a truncated response. Not an error in itself; the
    // status is what the caller acts on.
    return null;
  }
}

/**
 * One request, JSON in and out.
 *
 * Throws SessionExpiredError on 401/403 and ApiError on any other non-2xx, so
 * the happy path in a component is a plain `await`.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, headers: headersFor(path, init.headers) });
  const body = await parse(res);

  if (isHtml2xx(res)) throw new ApiError(res.status, null, `${path} answered HTML, not JSON`);

  const code = typeof body === "object" && body !== null
    ? (body as { error?: string; code?: string }).error ?? (body as { code?: string }).code
    : undefined;
  if ((res.status === 403 && code === "not_a_member")
    || (res.status === 401 && code === "No brand yet")
    || (res.status === 401 && (body as { code?: string } | null)?.code === "signup_incomplete")) {
    throw new MembershipRequiredError(res.status, body);
  }
  if (res.status === 401 || res.status === 403) throw new SessionExpiredError(res.status, body);
  if (!res.ok) {
    const message = typeof body === "object" && body !== null && "error" in body
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "PATCH",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

/** What `raw()` answers with: the status, and the body if it was JSON. */
export interface RawResponse<T> {
  status: number;
  body: T | null;
}

/**
 * A request whose status the caller handles itself.
 *
 * The pay sheet's settle step. It must NOT throw on 401 and must NOT redirect
 * to sign-in: it renders its own sentence per status and keeps the sheet open
 * so a second tap is safe. Same auth rule as everything else here - rule 1
 * applies to this path most of all, since it is the one that pays.
 */
export async function raw<T>(path: string, init: RequestInit = {}): Promise<RawResponse<T>> {
  const financial = init.method?.toUpperCase() === "POST" && path.endsWith("/confirm");
  if (financial) pendingFinancialRequests++;
  try {
    const res = await fetch(path, { ...init, headers: headersFor(path, init.headers) });
    // The one status this must never report is a 200 that is not really one. Every
    // caller of this helper is a money path, and the pay sheet turns 200 into
    // "Confirmed" - see isHtml2xx. Callers already treat a throw as "could not
    // confirm, try again", which is the honest reading.
    if (isHtml2xx(res)) throw new ApiError(res.status, null, `${path} answered HTML, not JSON`);
    return { status: res.status, body: (await parse(res)) as T | null };
  } finally {
    if (financial) pendingFinancialRequests--;
  }
}
